import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  BriefcaseBusinessIcon,
  CalendarClockIcons,
  OfficeIcons,
} from '../../../../../icons/icons';
import { JOB_PORTAL_KEYS, NS } from '../../../../../i18n/constants/keys';
import { ClockIcon } from '../../../../../ui/icons/icons';
import { JobPostingData } from '../../../../../utils/types/JobOpenings';

const extractRoleDetails = (htmlTemplate: string): string | null => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlTemplate, 'text/html');
  const roleDetailsTitle = Array.from(doc.querySelectorAll('p > strong')).find(
    (el) => el.textContent?.trim() === 'Role Details',
  );

  if (!roleDetailsTitle) return null;

  const roleDetailsContent: string[] = [];
  let sibling = roleDetailsTitle.parentElement?.nextElementSibling;

  while (sibling && !sibling.querySelector('strong')) {
    roleDetailsContent.push(sibling.textContent ?? '');
    sibling = sibling.nextElementSibling;
  }

  return roleDetailsContent.join(' ').trim();
};

type JobCardProps = {
  jobPostData: JobPostingData;
  viewMode?: 'grid' | 'list';
};

const JobCard: React.FC<JobCardProps> = ({ jobPostData, viewMode = 'grid' }) => {
  const { t } = useTranslation(NS.jobPortal);
  const history = useHistory();
  const roleDetails = extractRoleDetails(jobPostData.htmlTemplate);

  const handleApply = () => {
    history.push(`/jobs/${jobPostData.id}`);
  };

  const startDate = new Date(jobPostData.startDate * 1000).toLocaleDateString(
    'en-US',
    {
      month: 'short',
      day: 'numeric',
    },
  );

  const workTypeClasses =
    jobPostData.workType === 'Remote'
      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
      : jobPostData.workType === 'Office'
        ? 'border-blue-200 bg-blue-50 text-blue-700'
        : 'border-amber-200 bg-amber-50 text-amber-700';

  if (viewMode === 'list') {
    return (
      <article className='rounded-lg border border-slate-200 bg-white p-4 shadow-sm transition hover:border-emerald-200 hover:shadow-md'>
        <div className='grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_auto] xl:items-center'>
          <div className='flex min-w-0 gap-3'>
            <div className='flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700'>
              <BriefcaseBusinessIcon className='h-5 w-5' />
            </div>
            <div className='min-w-0'>
              <div className='mb-1 flex flex-wrap items-center gap-2'>
                <h3 className='truncate text-lg font-extrabold text-slate-950'>
                  { jobPostData.jobTitle }
                </h3>
                <span className='rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-slate-600'>
                  Open role
                </span>
              </div>
              <div className='flex flex-wrap items-center gap-2 text-xs font-semibold text-slate-500'>
                <span>{ jobPostData.department || 'General' }</span>
                <span className='h-1 w-1 rounded-full bg-slate-300' />
                <span>{ jobPostData.location || t(JOB_PORTAL_KEYS.card.remote) }</span>
                <span className='h-1 w-1 rounded-full bg-slate-300' />
                <span className='inline-flex items-center gap-1'>
                  <CalendarClockIcons />
                  { startDate }
                </span>
              </div>
              { roleDetails && (
                <p className='mt-2 line-clamp-2 max-w-4xl text-sm leading-6 text-slate-600'>
                  { roleDetails }
                </p>
              ) }
            </div>
          </div>

          <div className='flex flex-wrap items-center gap-2 xl:justify-end'>
            <span
                className={`inline-flex items-center gap-2 rounded-md border px-3 py-2 text-xs font-bold ${workTypeClasses}`}
            >
              <OfficeIcons className='h-4 w-4' />
              { jobPostData.workType || 'Work type' }
            </span>
            <span className='inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700'>
              <ClockIcon />
              { t(JOB_PORTAL_KEYS.card.fullTime) }
            </span>
            <button
                onClick={handleApply}
                className='inline-flex min-w-[128px] items-center justify-center rounded-md bg-slate-950 px-4 py-2 text-sm font-bold text-white shadow-sm transition hover:bg-emerald-700'
            >
              { t(JOB_PORTAL_KEYS.card.applyNow) }
            </button>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className='group flex h-full flex-col rounded-lg border border-slate-200 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:border-emerald-200 hover:shadow-md'>
      <div className='mb-4 flex items-start gap-3'>
        <div className='flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700'>
          <BriefcaseBusinessIcon className='h-5 w-5' />
        </div>
        <div className='min-w-0 flex-1'>
          <div className='mb-1 flex items-center gap-2'>
            <span className='h-2 w-2 rounded-full bg-emerald-400' />
            <span className='text-xs font-bold text-slate-500'>
              { jobPostData.department || 'General' }
            </span>
          </div>
          <h3 className='line-clamp-2 text-lg font-extrabold text-slate-950 group-hover:text-emerald-700'>
            { jobPostData.jobTitle }
          </h3>
        </div>
      </div>

      <div className='mb-4 grid grid-cols-1 gap-2 text-sm'>
        <div className='rounded-lg border border-slate-200 bg-slate-50 px-3 py-2'>
          <p className='text-[11px] font-bold uppercase text-slate-400'>
            { t(JOB_PORTAL_KEYS.card.location) }
          </p>
          <p className='truncate font-bold text-slate-900'>
            { jobPostData.location || t(JOB_PORTAL_KEYS.card.remote) }
          </p>
        </div>
        <div className='flex items-center gap-2 text-xs font-semibold text-slate-500'>
          <CalendarClockIcons />
          Posted { startDate }
        </div>
      </div>

      { roleDetails && (
        <p className='mb-4 line-clamp-3 text-sm leading-6 text-slate-600'>
          { roleDetails }
        </p>
      ) }

      <div className='mt-auto'>
        <div className='mb-3 flex flex-wrap gap-2'>
          <span
              className={`inline-flex items-center gap-2 rounded-md border px-3 py-2 text-xs font-bold ${workTypeClasses}`}
          >
            <OfficeIcons className='h-4 w-4' />
            { jobPostData.workType || 'Work type' }
          </span>
          <span className='inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700'>
            <ClockIcon />
            { t(JOB_PORTAL_KEYS.card.fullTime) }
          </span>
        </div>
        <button
            onClick={handleApply}
            className='inline-flex w-full items-center justify-center rounded-md bg-slate-950 px-4 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-emerald-700'
        >
          { t(JOB_PORTAL_KEYS.card.applyNow) }
        </button>
      </div>
    </article>
  );
};

export default JobCard;
